import { ArrowRight, Calendar, CircleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  type AppointmentTypeId,
  addCalendarDays,
  CLINICS_BY_ID,
  type ClinicId,
  calculateAppointmentDuration,
  DEFAULT_TIMEZONE,
  isAppointmentTypeId,
  isMemberAppointmentChangeWindowClosed,
  toWallClockDate,
} from '@snootsvet/shared/portal'

import { AppointmentChangeWindowClosedAlert } from '@/customer/components/booking/appointment-change-window-closed-alert'
import { BookingContextCard } from '@/customer/components/booking/booking-context-card'
import { BookingNotes } from '@/customer/components/booking/booking-notes'
import { getMondayOfWeek, WeekSlotPicker } from '@/customer/components/booking/week-slot-picker'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PageContainer } from '@/customer/components/page-container'
import { Alert, AlertAction, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetAvailableSlots } from '@/customer/hooks/use-get-available-slots'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { useListAppointments } from '@/customer/hooks/use-list-appointments'
import { useRegion } from '@/customer/hooks/use-region'
import { useUpdateAppointment } from '@/customer/hooks/use-update-appointment'
import { routes } from '@/customer/routes'

/**
 * Portal reschedule page. Reached from the manage-appointment dialog:
 *
 *   /account/appointments/:appointmentId/reschedule
 *
 * Mirrors `book-appointment-page.tsx`'s slot-picker layout but reads
 * `type` / `clinic` / `patient` from the existing appointment instead
 * of URL query params, and fires `PATCH /portal/appointments/:id`
 * with `action: 'reschedule'` instead of `POST`.
 *
 * Kept as its own page rather than a mode toggle on the book page:
 * the URL contracts, guards, and success semantics diverge enough
 * that branching inside a single component would be muddier than
 * two lean pages sharing the same `WeekSlotPicker` + `BookingContextCard`
 * primitives.
 */
export function ReschedulingAppointmentPage() {
  useDocumentTitle('Reschedule visit')

  const { appointmentId = '' } = useParams<{ appointmentId: string }>()
  const me = useGetMe()
  const appointments = useListAppointments()

  if (me.isPending || !me.data || appointments.isPending || !appointments.data) {
    return <LoadingSpinner />
  }

  // Guards. The appointment must exist in the member's list, still be
  // in the future, and not already cancelled - any other state is
  // handled by staff, not self-service.
  const appointment = appointments.data.appointments.find((a) => a.id === appointmentId)
  const patient = appointment ? me.data.patients.find((p) => p.id === appointment.patientId) : null
  const clinic = appointment?.clinicId ? CLINICS_BY_ID[appointment.clinicId] : null

  if (
    !appointment ||
    !patient ||
    !clinic ||
    !isAppointmentTypeId(appointment.appointmentTypeId) ||
    appointment.status === 'cancelled' ||
    new Date(appointment.startTime).getTime() < Date.now()
  ) {
    return <Navigate to={routes.account.index} replace />
  }

  if (isMemberAppointmentChangeWindowClosed(appointment.startTime)) {
    return (
      <PageContainer
        backLink={{ to: routes.account.index, label: 'Back' }}
        headline={[
          { text: 'Reschedule', color: 'green' },
          { text: `${patient.name}'s`, color: 'yellow' },
          { text: 'appointment', color: 'blue' },
        ]}
      >
        <AppointmentChangeWindowClosedAlert clinicId={clinic.id} />
      </PageContainer>
    )
  }

  return (
    <RescheduleAppointmentPageInner
      appointmentId={appointment.id}
      patientName={patient.name}
      clinicId={clinic.id}
      timezone={clinic.timezone ?? DEFAULT_TIMEZONE}
      appointmentTypeId={appointment.appointmentTypeId}
      existingStart={new Date(appointment.startTime)}
      durationMinutes={calculateAppointmentDuration(appointment)}
      existingNotes={appointment.userNotes ?? ''}
    />
  )
}

/**
 * Inner half of the reschedule page. Split out so the guards can
 * early-return before we mount the slot / update hooks, matching the
 * shape used by `BookAppointmentPage`.
 */
function RescheduleAppointmentPageInner({
  appointmentId,
  patientName,
  clinicId,
  timezone,
  appointmentTypeId,
  existingStart,
  durationMinutes,
  existingNotes,
}: {
  appointmentId: string
  patientName: string
  clinicId: ClinicId
  timezone: string
  appointmentTypeId: AppointmentTypeId
  existingStart: Date
  durationMinutes: number
  existingNotes: string
}) {
  const navigate = useNavigate()
  const region = useRegion()
  const updateAppointment = useUpdateAppointment(appointmentId)

  // Anchored on the clinic's current date, not the browser's, so a
  // member browsing from another timezone still lands on the week the
  // clinic is in.
  const minWeekStart = useMemo(
    () => getMondayOfWeek(toWallClockDate(new Date(), timezone)),
    [timezone]
  )
  const [weekStart, setWeekStart] = useState(minWeekStart)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [notes, setNotes] = useState(existingNotes)

  const slotsQuery = useGetAvailableSlots({
    appointmentId,
    startDate: weekStart,
    endDate: addCalendarDays(weekStart, 7),
  })

  const handleConfirm = () => {
    if (!selectedSlot) return
    const trimmedNotes = notes.trim()
    // Notes patch semantics on the shared schema:
    //   - `undefined` = leave whatever the server has
    //   - `null`      = clear a previously-set note
    //   - string      = set to that value
    //
    // Only send a value when the member actually changed the field.
    // If they opened the page and hit confirm without touching the
    // textarea, we skip the note entirely so we don't overwrite any
    // staff-side edit that landed between page load and submit.
    const notesChanged = trimmedNotes !== existingNotes.trim()
    const userNotes = !notesChanged ? undefined : trimmedNotes ? trimmedNotes : null
    updateAppointment.mutate(
      {
        action: 'reschedule',
        startTime: selectedSlot,
        userNotes,
      },
      {
        onSuccess: () => {
          navigate(routes.account.index)
        },
      }
    )
  }

  const updateError = updateAppointment.error
  const isSubmitting = updateAppointment.isPending

  return (
    <PageContainer
      backLink={{ to: routes.account.index, label: 'Back' }}
      headline={[
        { text: 'Reschedule', color: 'green' },
        { text: `${patientName}'s`, color: 'yellow' },
        { text: 'appointment', color: 'blue' },
      ]}
    >
      <div className="flex flex-col gap-8">
        <BookingContextCard
          appointmentTypeId={appointmentTypeId}
          clinicId={clinicId}
          start={existingStart}
          durationMinutes={durationMinutes}
          region={region}
          timezone={timezone}
        />

        {slotsQuery.isError ? (
          <Alert variant="destructive">
            <AlertTitle>We couldn't load appointment availability.</AlertTitle>
            <AlertAction>
              <button
                type="button"
                className="group inline-flex cursor-pointer items-center gap-1 text-button-sm underline underline-offset-2 hover:no-underline"
                onClick={() => void slotsQuery.refetch()}
              >
                Try again
                <ArrowRight
                  className="size-3 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </button>
            </AlertAction>
          </Alert>
        ) : (
          <WeekSlotPicker
            slots={slotsQuery.data?.slots}
            selectedSlot={selectedSlot}
            onSelectSlot={setSelectedSlot}
            weekStart={weekStart}
            onWeekChange={setWeekStart}
            minWeekStart={minWeekStart}
            timezone={timezone}
            region={region}
            loading={slotsQuery.isFetching}
          />
        )}

        <BookingNotes id="reschedule-notes" value={notes} onChange={setNotes} />

        {updateError ? (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>
              {updateError.message || 'Something went wrong. Please try again.'}
            </AlertTitle>
          </Alert>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row-reverse">
          <Button
            type="button"
            className="w-full sm:flex-1"
            disabled={!selectedSlot || isSubmitting}
            onClick={handleConfirm}
          >
            <Calendar data-icon="inline-start" />
            {isSubmitting ? 'Rescheduling…' : 'Confirm new time'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full sm:flex-1"
            onClick={() => navigate(routes.account.index)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </div>
    </PageContainer>
  )
}
